import { FormProps, FormTypes } from '../../shared/interfaces/form';
import FormInput from './components/form-input';
import FormSelect from './components/form-select';
import FormTextarea from './components/form-textarea';
import FormDate from './components/form-date';
import FormCurrency from './components/form-currency';
import FormImage from './components/form-image';
import FormCheckbox from './components/form-checkbox';
import FormSelectGroup from './components/form-select-group';
import { useWatch } from 'react-hook-form';
import { CreateRubricaPayload, RubricaTypeOption } from '../modals/rubrica/create-rubrica-modal';
import { PendingRubricaOption } from '../modals/rubrica/rubrica-modal';

interface FormFieldProps {
  item: FormProps;
  index: number;
  baseId: string;
  errors: any;
  control: any;
  isEditor: boolean;
  isFinalLoading: boolean;
  handleDurationBlur?: (e: any) => void;
  handleDateChange?: () => void;
  handleCheckboxChange?: (value: boolean, fieldName: string) => void;
  setValue: (name: string, value: any) => void;
  getValues: (name?: string | string[]) => any;
  onClickButton?: () => void;
  rubricaTypes?: RubricaTypeOption[];
  pendingRubricas?: PendingRubricaOption[];
  onCreatePendingRubrica?: (payload: CreateRubricaPayload) => PendingRubricaOption;
}

const FormField = ({
  item,
  index,
  baseId,
  errors,
  control,
  isEditor,
  isFinalLoading,
  handleDurationBlur,
  handleDateChange,
  handleCheckboxChange,
  setValue,
  getValues,
  onClickButton,
  rubricaTypes = [],
  pendingRubricas = [],
  onCreatePendingRubrica,
}: FormFieldProps) => {
  const isDisabled = !isEditor || isFinalLoading || item.disabled ;
  const visibleFieldValue = useWatch({
    control,
    name: item.visibleWhen?.fieldName || `__${item.name}_visibility__`,
  });

  if (item.visibleWhen && !item.visibleWhen.values.includes(visibleFieldValue)) {
    return null;
  }
  
  switch (item.typeOfField) {
    case FormTypes.INPUT:
      return <FormInput 
        item={item} 
        index={index} 
        baseId={baseId} 
        errors={errors} 
        control={control} 
        isDisabled={item.disabled || isDisabled || false}
        handleDurationBlur={handleDurationBlur}
        setValue={setValue}
        getValues={getValues}
      />;
    
    case FormTypes.SELECT:
      return <FormSelect 
        item={item} 
        index={index} 
        baseId={baseId} 
        errors={errors} 
        control={control} 
        isDisabled={isDisabled || item.disabled || false}
        setValue={setValue}
        getValues={getValues}
      />;
    
    case FormTypes.TEXTAREA:
      return <FormTextarea 
        item={item} 
        index={index} 
        baseId={baseId} 
        errors={errors} 
        control={control} 
        isDisabled={isDisabled || item.disabled || false}
        setValue={setValue}
        getValues={getValues}
      />;
    
    case FormTypes.DATE:
      return <FormDate 
        item={item} 
        index={index} 
        baseId={baseId} 
        errors={errors} 
        control={control} 
        isDisabled={isDisabled || item.disabled || false}
        setValue={setValue}
        getValues={getValues}
        handleDateChanged={handleDateChange}
      />;
    
    case FormTypes.CURRENCY:
      return <FormCurrency 
        item={item} 
        index={index} 
        baseId={baseId} 
        errors={errors} 
        control={control} 
        isDisabled={isDisabled || item.disabled || false}
        setValue={setValue}
        getValues={getValues}
      />;
    
    case FormTypes.IMAGE:
      return <FormImage 
        item={item} 
        index={index} 
        baseId={baseId} 
        errors={errors} 
        control={control} 
        isDisabled={isDisabled || item.disabled || false}
        setValue={setValue}
        getValues={getValues}
      />;
    
    case FormTypes.CHECKBOX:
      return <FormCheckbox 
        item={item} 
        index={index} 
        baseId={baseId} 
        errors={errors} 
        control={control} 
        isDisabled={isDisabled || item.disabled || false}
        setValue={setValue}
        getValues={getValues}
        handleCheckboxChange={handleCheckboxChange}
      />;

    case FormTypes.GROUPED_SELECT:
      return <FormSelectGroup 
        item={item} 
        index={index} 
        baseId={baseId} 
        errors={errors} 
        control={control} 
        isDisabled={isDisabled || item.disabled || false}
        setValue={setValue}
        getValues={getValues}
        rubricaTypes={rubricaTypes}
        pendingRubricas={pendingRubricas}
        onCreatePendingRubrica={onCreatePendingRubrica}
      />;
    
    default:
      return null;
  }
};

export default FormField;
